import { Link, useLocation } from 'react-router-dom';
import { Logo } from './Logo';
import { PlayStoreBadge } from './PlayStoreBadge';
import { getFleetTrialSignupUrl, getPlayStoreUrl } from '../../config/urls';
import { Analytics } from '../../services/analytics';

export function Navbar() {
  const location = useLocation();
  const playStoreUrl = getPlayStoreUrl();
  const trialUrl = getFleetTrialSignupUrl();
  const isFleetPage = location.pathname === '/fleet' || location.pathname.startsWith('/fleet/') || location.pathname === '/pco-hire-pcn-management';
  const isFleetLandingActive = location.pathname === '/fleet' && location.hash !== '#pricing';
  const isFleetFeaturesActive = location.pathname === '/fleet/features';
  const isPricingActive = location.pathname === '/fleet' && location.hash === '#pricing';

  const fleetNavLinkClass = (active: boolean) =>
    [
      'px-4 py-2 rounded-md text-sm font-bold transition-colors',
      active
        ? 'bg-[#eaf3ee] text-[#194d3a] shadow-sm shadow-[#194d3a]/10'
        : 'text-[#344347] hover:text-[#194d3a] hover:bg-[#f6f0e6]',
    ].join(' ');

  const handlePlayClick = (placement: string) => {
    Analytics.track('landing_cta_play_click', {
      placement,
      audience: 'driver'
    });
  };

  const handleFleetClick = (placement: string) => {
    Analytics.track('landing_cta_fleet_click', {
      placement,
      audience: 'fleet'
    });
  };

  const handleTrialClick = (placement: string) => {
    Analytics.track('fleet_trial_cta_clicked', {
      placement,
      destination: trialUrl,
      audience: 'pco_hire'
    });
  };

  return (
    <nav className="fixed top-0 w-full z-40 bg-[#fffaf1]/92 backdrop-blur-md border-b border-[#e4d8c8]">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:rounded-md focus:bg-[#194d3a] focus:text-white focus:text-sm focus:font-bold"
      >
        Skip to main content
      </a>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-3">
        <Link to="/" className="flex min-w-0 items-center gap-3 group">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-white flex items-center justify-center shadow-sm border border-[#e7dccd] group-hover:scale-105 group-hover:shadow-md transition-all duration-300 p-1 overflow-hidden">
            <Logo className="w-full h-full object-contain scale-150" />
          </div>
          <span className="min-w-0 truncate font-bold text-lg sm:text-xl tracking-tight font-space text-[#081b1f]">
            PCNGuard
          </span>
        </Link>
        <div className="hidden md:flex items-center gap-6">
          {isFleetPage ? (
            <>
              <Link
                to="/"
                className="px-4 py-2 rounded-md text-[#344347] text-sm font-bold hover:text-[#194d3a] transition-colors"
              >
                Driver App
              </Link>
              <Link
                to="/fleet"
                className={fleetNavLinkClass(isFleetLandingActive)}
                aria-current={isFleetLandingActive ? 'page' : undefined}
              >
                PCO Hire
              </Link>
              <Link
                to="/fleet/features"
                className={fleetNavLinkClass(isFleetFeaturesActive)}
                aria-current={isFleetFeaturesActive ? 'page' : undefined}
              >
                Features
              </Link>
              <Link
                to="/fleet#pricing"
                className={fleetNavLinkClass(isPricingActive)}
                aria-current={isPricingActive ? 'location' : undefined}
              >
                Pricing
              </Link>
              <a
                href={trialUrl}
                onClick={() => handleTrialClick('nav')}
                className="px-4 py-2 rounded-md bg-[#194d3a] text-white text-sm font-bold hover:bg-[#26654e] active:translate-y-px transition-all duration-200 shadow-sm shadow-[#194d3a]/25"
              >
                Check 3 PCNs free
              </a>
            </>
          ) : (
            <>
              <Link
                to="/fleet"
                onClick={() => handleFleetClick('nav')}
                className="px-4 py-2 rounded-md bg-[#194d3a] text-white text-sm font-bold hover:bg-[#26654e] active:translate-y-px transition-all duration-200 shadow-sm shadow-[#194d3a]/25"
              >
                PCO Hire
              </Link>
              <PlayStoreBadge href={playStoreUrl || undefined} onClick={() => handlePlayClick('nav')} className="h-10" />
            </>
          )}
        </div>
        <div className="md:hidden shrink-0">
          <Link
            to={isFleetPage ? '/' : '/fleet'}
            onClick={() => {
              if (!isFleetPage) handleFleetClick('nav_mobile');
            }}
            className="inline-flex items-center justify-center px-3 sm:px-4 py-2 rounded-md bg-[#194d3a] text-white text-xs sm:text-sm font-bold whitespace-nowrap hover:bg-[#26654e] active:translate-y-px transition-all duration-200"
          >
            <span className="min-[420px]:hidden">{isFleetPage ? 'Driver' : 'PCO'}</span>
            <span className="hidden min-[420px]:inline">{isFleetPage ? 'Driver App' : 'PCO Hire'}</span>
          </Link>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
